import type { InventoryInstanceWire, InventoryResponse, TradeWire } from '@kenocyber/shared';
import {
  DIRECT_TRADE_FEE_POINTS,
  TRADE_BLOCK_MESSAGES,
  TRADE_STATUS_LABELS,
} from '@kenocyber/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  acceptTradeOffer,
  cancelTrade,
  fetchInventory,
  fetchTrade,
  fetchTradeInbox,
  finalConfirmTrade,
  HuntApiError,
  inviteToTrade,
  respondToTrade,
  searchTradePlayers,
  setTradeFeeContribution,
  setTradeOffer,
} from '../api/hunt';
import { formatDecimal } from './format';
import { rarityLabel } from './rarity';

/**
 * The direct trade, as a floating panel over the arena.
 *
 * ## It never touches the hunt
 *
 * `position: absolute` like every other panel, so opening it does not resize
 * or recreate the Phaser canvas. It calls no bridge message, holds no simulation
 * state and does not pause anything: the wave, the boss countdown and the shots
 * in flight belong to the run, not to this dialogue.
 *
 * ## Nothing is predicted
 *
 * There is no optimistic transfer anywhere in this file. A trade moves real
 * items and real currency, so every number drawn here came from the server's
 * last answer, and every action re-reads. A client that guessed would be
 * showing somebody an item they do not own yet.
 *
 * ## Polling, and when it stops
 *
 * The other player's side changes without this client doing anything, so the
 * open trade is re-read on a modest interval. It stops when the tab is hidden
 * and when the panel closes: a background tab has nothing to draw, and polling
 * from one is a request the player did not ask for.
 */

const POLL_INTERVAL_MS = 3000;

type TabKey = 'items' | 'gold' | 'cash' | 'fee';

const TABS: readonly { key: TabKey; label: string }[] = [
  { key: 'items', label: 'ITENS' },
  { key: 'gold', label: 'ENVIAR GOLD' },
  { key: 'cash', label: 'ENVIAR CASH' },
  { key: 'fee', label: 'PAGAR TAXA' },
];

interface TradePanelProps {
  readonly onClose: () => void;
  /**
   * Told when a trade settles, so the rest of the app can re-read the vault.
   *
   * Items changed hands. Nothing in the HUNT changed - no session, no wave, no
   * boss - so this is a vault refresh and nothing more.
   */
  readonly onSettled: () => void;
}

/** A decimal string field that only ever holds digits. */
function digitsOnly(value: string): string {
  return value.replace(/\D/g, '').replace(/^0+(?=\d)/, '') || '0';
}

export function TradePanel({ onClose, onSettled }: TradePanelProps) {
  // The vault is read HERE rather than threaded in: the item tab is the only
  // thing that needs it, and it has to be re-read after a settlement anyway.
  const [inventory, setInventory] = useState<InventoryResponse | null>(null);
  const [inbox, setInbox] = useState<
    { tradeId: string; state: string; counterpartyName: string; incoming: boolean }[]
  >([]);
  const [trade, setTrade] = useState<TradeWire | null>(null);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<
    { accountId: string; displayName: string; vipActive: boolean }[]
  >([]);
  const [tab, setTab] = useState<TabKey>('items');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  /** Draft fields, so typing does not fire a request per keystroke. */
  const [offerGold, setOfferGold] = useState('0');
  const [offerCash, setOfferCash] = useState('0');
  const [feeGold, setFeeGold] = useState('0');
  const [feeCash, setFeeCash] = useState('0');

  const openTradeId = useRef<string | null>(null);

  const report = (unknownError: unknown) => {
    setError(
      unknownError instanceof HuntApiError
        ? (unknownError.reason ?? 'Falha na operação')
        : 'Falha na operação',
    );
  };

  /** Adopts a server answer wholesale. The panel never merges its own guess. */
  const adopt = useCallback((next: TradeWire) => {
    setTrade(next);
    openTradeId.current = next.tradeId;
    const mine =
      next.initiator.accountId === next.viewerAccountId ? next.initiator : next.counterparty;
    setOfferGold(mine.offerGold);
    setOfferCash(mine.offerCash);
    setFeeGold(mine.feeGold);
    setFeeCash(mine.feeCash);
  }, []);

  const refreshInbox = useCallback(async () => {
    try {
      const response = await fetchTradeInbox();
      setInbox(
        response.trades.map((row) => ({
          tradeId: row.tradeId,
          state: row.state,
          counterpartyName: row.counterpartyName,
          incoming: row.incoming,
        })),
      );
    } catch {
      // A failed read leaves the previous list on screen; the next poll fixes it.
    }
  }, []);

  const refreshInventory = useCallback(async () => {
    try {
      setInventory(await fetchInventory());
    } catch {
      // A failed read leaves the previous list on screen; the tab says so.
    }
  }, []);

  useEffect(() => {
    void refreshInbox();
    void refreshInventory();
  }, [refreshInbox, refreshInventory]);

  /**
   * Re-reads the open trade while the panel is visible.
   *
   * Stops when the tab is hidden and when the panel unmounts. A background tab
   * has nothing to draw, so polling from one is a request nobody asked for.
   */
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const tradeId = openTradeId.current;
      if (!tradeId || document.hidden || cancelled) {
        return;
      }
      try {
        const response = await fetchTrade(tradeId);
        if (!cancelled) {
          adopt(response.trade);
        }
      } catch {
        // Transient. The panel keeps the last authoritative answer.
      }
    };
    const timer = window.setInterval(() => void tick(), POLL_INTERVAL_MS);
    const onVisibility = () => {
      if (!document.hidden) {
        void tick();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [adopt]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const run = async (work: () => Promise<{ trade: TradeWire }>) => {
    setBusy(true);
    setError(null);
    try {
      adopt((await work()).trade);
      await refreshInbox();
      // Reserving, removing, cancelling and completing all change which copies
      // are eligible, so the item tab is re-read from the vault rather than
      // guessed at locally.
      await refreshInventory();
    } catch (unknownError) {
      report(unknownError);
      // Re-read after a refusal: a revision conflict means the screen was
      // stale, and the honest fix is the server's current answer.
      const tradeId = openTradeId.current;
      if (tradeId) {
        try {
          adopt((await fetchTrade(tradeId)).trade);
        } catch {
          /* keep what is on screen */
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const mine = trade
    ? trade.initiator.accountId === trade.viewerAccountId
      ? trade.initiator
      : trade.counterparty
    : null;
  const theirs = trade
    ? trade.initiator.accountId === trade.viewerAccountId
      ? trade.counterparty
      : trade.initiator
    : null;

  const editable = trade?.state === 'open';
  const feeComplete = trade?.fee.complete ?? false;

  return (
    <aside className="trade-panel" data-testid="trade-panel" aria-label="Trade direto">
      <header className="trade-panel-header">
        <h2>Trade direto</h2>
        <button
          type="button"
          className="overview-close"
          data-testid="trade-close"
          aria-label="Fechar trade"
          onClick={onClose}
        >
          ×
        </button>
      </header>

      {error ? (
        <p className="trade-error" role="alert" data-testid="trade-error">
          {error === 'vip_required'
            ? 'VIP NECESSÁRIO PARA CONCLUIR O TRADE'
            : error === 'fee_incomplete'
              ? 'A taxa ainda não está completa.'
              : error === 'revision_conflict'
                ? 'A oferta mudou. Confira o resumo atualizado.'
                : error}
        </p>
      ) : null}

      {!trade ? (
        <section className="trade-lobby">
          <h3>Convidar jogador</h3>
          <div className="trade-search">
            <input
              type="text"
              value={search}
              placeholder="Nome do jogador"
              data-testid="trade-search-input"
              onChange={(event) => setSearch(event.target.value)}
            />
            <button
              type="button"
              className="ghost-button"
              data-testid="trade-search-button"
              disabled={search.trim().length < 3 || busy}
              onClick={async () => {
                try {
                  setResults((await searchTradePlayers(search.trim())).players);
                } catch (unknownError) {
                  report(unknownError);
                }
              }}
            >
              Buscar
            </button>
          </div>
          <ul className="trade-results">
            {results.map((player) => (
              <li key={player.accountId}>
                <span>{player.displayName}</span>
                {/* Both sides need VIP, so a player who cannot trade is shown
                    as such rather than failing after the invitation. */}
                <span className={player.vipActive ? 'trade-vip' : 'trade-no-vip'}>
                  {player.vipActive ? 'VIP' : 'SEM VIP'}
                </span>
                <button
                  type="button"
                  className="neon-button"
                  disabled={!player.vipActive || busy}
                  onClick={() => void run(() => inviteToTrade(player.accountId))}
                >
                  Convidar
                </button>
              </li>
            ))}
          </ul>

          <h3>Recebidos</h3>
          <ul className="trade-results" data-testid="trade-inbox">
            {inbox.length === 0 ? <li>Nenhum trade.</li> : null}
            {inbox.map((row) => (
              <li key={row.tradeId}>
                <span>{row.counterpartyName}</span>
                <span className="trade-state">{row.state}</span>
                <button
                  type="button"
                  className="ghost-button"
                  disabled={busy}
                  onClick={() => void run(async () => await fetchTrade(row.tradeId))}
                >
                  Abrir
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {trade && mine && theirs ? (
        <section className="trade-body">
          <div className="trade-meta">
            <span data-testid="trade-state">{trade.state}</span>
            <span data-testid="trade-revision">rev {trade.revision}</span>
            <span>{theirs.displayName}</span>
            {!theirs.vipActive || !mine.vipActive ? (
              <strong className="trade-no-vip">VIP NECESSÁRIO PARA CONCLUIR O TRADE</strong>
            ) : null}
          </div>

          {trade.state === 'invited' && trade.counterparty.accountId === trade.viewerAccountId ? (
            <div className="trade-actions">
              <button
                type="button"
                className="neon-button"
                disabled={busy}
                onClick={() => void run(() => respondToTrade(trade.tradeId, true))}
              >
                Aceitar convite
              </button>
              <button
                type="button"
                className="ghost-button"
                disabled={busy}
                onClick={() => void run(() => respondToTrade(trade.tradeId, false))}
              >
                Recusar
              </button>
            </div>
          ) : null}

          <div className="trade-columns">
            <div className="trade-column" data-testid="trade-mine">
              <h3>SUA OFERTA</h3>
              <OfferSummary
                items={mine.items}
                gold={mine.offerGold}
                cash={mine.offerCash}
                accepted={mine.acceptedRevision === trade.revision}
              />
            </div>
            <div className="trade-column" data-testid="trade-theirs">
              <h3>OFERTA DO OUTRO</h3>
              <OfferSummary
                items={theirs.items}
                gold={theirs.offerGold}
                cash={theirs.offerCash}
                accepted={theirs.acceptedRevision === trade.revision}
              />
            </div>
          </div>

          {/* Four tabs, and Gold and Cash never share a field: they are
              different currencies and a single input would invite mistakes
              measured in real money. */}
          <div className="trade-tabs" role="tablist">
            {TABS.map((entry) => (
              <button
                key={entry.key}
                type="button"
                role="tab"
                aria-selected={tab === entry.key}
                className={tab === entry.key ? 'is-active' : ''}
                data-testid={`trade-tab-${entry.key}`}
                onClick={() => setTab(entry.key)}
              >
                {entry.label}
              </button>
            ))}
          </div>

          {tab === 'items' ? (
            <ItemsTab
              inventory={inventory}
              chosen={mine.items.map((item) => item.instanceId).filter(Boolean) as string[]}
              disabled={!editable || busy}
              onToggle={(instanceId) => {
                const chosen = new Set(
                  mine.items.map((item) => item.instanceId).filter(Boolean) as string[],
                );
                if (chosen.has(instanceId)) {
                  chosen.delete(instanceId);
                } else {
                  chosen.add(instanceId);
                }
                void run(() =>
                  setTradeOffer({
                    tradeId: trade.tradeId,
                    expectedRevision: trade.revision,
                    items: [...chosen].map((id) => ({ kind: 'instance' as const, instanceId: id })),
                    gold: mine.offerGold,
                    cash: mine.offerCash,
                  }),
                );
              }}
            />
          ) : null}

          {tab === 'gold' || tab === 'cash' ? (
            <CurrencyTab
              kind={tab}
              wallet={trade.wallets.find((wallet) => wallet.currency === tab)}
              value={tab === 'gold' ? offerGold : offerCash}
              disabled={!editable || busy}
              onChange={(value) =>
                tab === 'gold' ? setOfferGold(digitsOnly(value)) : setOfferCash(digitsOnly(value))
              }
              onCommit={() =>
                void run(() =>
                  setTradeOffer({
                    tradeId: trade.tradeId,
                    expectedRevision: trade.revision,
                    items: mine.items
                      .filter((item) => item.instanceId)
                      .map((item) => ({
                        kind: 'instance' as const,
                        instanceId: item.instanceId as string,
                      })),
                    gold: tab === 'gold' ? offerGold : mine.offerGold,
                    cash: tab === 'cash' ? offerCash : mine.offerCash,
                  }),
                )
              }
            />
          ) : null}

          {tab === 'fee' ? (
            <FeeTab
              trade={trade}
              mineFeePoints={mine.feePoints}
              theirFeePoints={theirs.feePoints}
              gold={feeGold}
              cash={feeCash}
              disabled={!editable || busy}
              onGold={(value) => setFeeGold(digitsOnly(value))}
              onCash={(value) => setFeeCash(digitsOnly(value))}
              onCommit={() =>
                void run(() =>
                  setTradeFeeContribution({
                    tradeId: trade.tradeId,
                    expectedRevision: trade.revision,
                    gold: feeGold,
                    cash: feeCash,
                  }),
                )
              }
            />
          ) : null}

          {mine.receivesNothing ? (
            <div className="trade-empty-warning" data-testid="trade-empty-warning">
              <p>VOCÊ NÃO RECEBERÁ ITENS, GOLD OU CASH NESTA TROCA.</p>
              <label>
                <input
                  type="checkbox"
                  checked={acknowledged}
                  data-testid="trade-empty-ack"
                  onChange={(event) => setAcknowledged(event.target.checked)}
                />
                ENTENDO QUE O OUTRO LADO ESTÁ VAZIO.
              </label>
            </div>
          ) : null}

          <footer className="trade-footer">
            <button
              type="button"
              className="neon-button"
              data-testid="trade-accept"
              // The fee is part of what is being agreed to, so accepting is
              // impossible until it adds up exactly.
              disabled={
                !editable || busy || !feeComplete || (mine.receivesNothing && !acknowledged)
              }
              onClick={() =>
                void run(() =>
                  acceptTradeOffer({
                    tradeId: trade.tradeId,
                    expectedRevision: trade.revision,
                    acknowledgeEmptySide: acknowledged,
                  }),
                )
              }
            >
              ACEITAR OFERTA
            </button>
            <button
              type="button"
              className="neon-button"
              data-testid="trade-confirm"
              disabled={trade.state !== 'locked' || busy}
              onClick={() =>
                void run(async () => {
                  const response = await finalConfirmTrade(trade.tradeId, trade.revision);
                  if (response.trade.state === 'completed') {
                    // Items changed hands: the vault has to be re-read, here
                    // and by the rest of the app. The hunt is untouched -
                    // nothing in this file talks to the scene.
                    void refreshInventory();
                    onSettled();
                  }
                  return response;
                })
              }
            >
              CONFIRMAR TROCA FINAL
            </button>
            <button
              type="button"
              className="ghost-button"
              data-testid="trade-cancel"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  const response = await cancelTrade(trade.tradeId);
                  setTrade(null);
                  openTradeId.current = null;
                  return response;
                })
              }
            >
              Cancelar
            </button>
          </footer>
        </section>
      ) : null}
    </aside>
  );
}

function OfferSummary({
  items,
  gold,
  cash,
  accepted,
}: {
  items: TradeWire['initiator']['items'];
  gold: string;
  cash: string;
  accepted: boolean;
}) {
  return (
    <div className="trade-offer">
      <ul>
        {items.length === 0 ? <li className="trade-empty">Vazio</li> : null}
        {items.map((item) => (
          <li key={item.instanceId ?? item.definitionId} className={`rarity-${item.rarity}`}>
            <span>{item.definitionId}</span>
            <span>{rarityLabel(item.rarity)}</span>
            <span>Nv {item.level}</span>
            {item.kind === 'stack' ? <span>×{formatDecimal(item.quantity)}</span> : null}
          </li>
        ))}
      </ul>
      <p>Gold: {formatDecimal(gold)}</p>
      <p>Cash: {formatDecimal(cash)}</p>
      <p className={accepted ? 'trade-accepted' : 'trade-pending'}>
        {accepted ? 'ACEITOU' : 'aguardando'}
      </p>
    </div>
  );
}

function ItemsTab({
  inventory,
  chosen,
  disabled,
  onToggle,
}: {
  inventory: InventoryResponse | null;
  chosen: readonly string[];
  disabled: boolean;
  onToggle: (instanceId: string) => void;
}) {
  if (!inventory) {
    return <p className="trade-hint">Carregando inventário…</p>;
  }
  // Eligible by default. `availableForTrade` is the SERVER's single verdict,
  // covering bound, equipped, active, held and non-tradable alike - and a copy
  // THIS trade already holds counts as eligible, because removing it is the
  // whole reason it is still on screen.
  const eligible = inventory.instances.filter(
    (item) => item.availableForTrade || chosen.includes(item.itemId),
  );
  const ineligible = inventory.instances.filter(
    (item) => !item.availableForTrade && !chosen.includes(item.itemId),
  );

  const row = (item: InventoryInstanceWire, picked: boolean) => (
    <li key={item.itemId} className={`rarity-${item.rarity}${picked ? ' is-picked' : ''}`}>
      <span>{item.definitionId}</span>
      <span>{rarityLabel(item.rarity)}</span>
      <span>Nv {item.level}</span>
      <span className="trade-tag">
        {item.availableForTrade
          ? TRADE_STATUS_LABELS.tradable
          : TRADE_STATUS_LABELS[item.unavailableReason ?? 'unavailable']}
      </span>
      <button
        type="button"
        className="ghost-button"
        // The server refuses an ineligible copy anyway; disabling here only
        // saves a round trip and lets the reason be shown instead.
        disabled={disabled || (!picked && !item.availableForTrade)}
        onClick={() => onToggle(item.itemId)}
      >
        {picked ? 'Remover' : 'Oferecer'}
      </button>
    </li>
  );

  return (
    <div className="trade-items-tab">
      <ul className="trade-items" data-testid="trade-items">
        {eligible.length === 0 ? <li className="trade-empty">Nenhum item elegível.</li> : null}
        {eligible.map((item) => row(item, chosen.includes(item.itemId)))}
      </ul>

      {/* The rest, behind a toggle, each saying WHY. Hiding them entirely
          would leave a player hunting for an item they can see everywhere
          else in the game. */}
      {ineligible.length > 0 ? (
        <details className="trade-ineligible" data-testid="trade-ineligible">
          <summary>Indisponíveis ({ineligible.length})</summary>
          <ul className="trade-items">
            {ineligible.map((item) => (
              <li key={item.itemId} className={`rarity-${item.rarity} is-blocked`}>
                <span>{item.definitionId}</span>
                <span>{rarityLabel(item.rarity)}</span>
                <span>Nv {item.level}</span>
                <span className="trade-block-reason">
                  {TRADE_BLOCK_MESSAGES[item.unavailableReason ?? 'unavailable']}
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

function CurrencyTab({
  kind,
  wallet,
  value,
  disabled,
  onChange,
  onCommit,
}: {
  kind: 'gold' | 'cash';
  wallet: TradeWire['wallets'][number] | undefined;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
  onCommit: () => void;
}) {
  const label = kind === 'gold' ? 'Gold' : 'Cash';
  return (
    <div className="trade-currency" data-testid={`trade-currency-${kind}`}>
      <p>
        {label} total: <strong>{formatDecimal(wallet?.total ?? '0')}</strong>
      </p>
      <p>
        {label} reservado:{' '}
        <strong>
          {formatDecimal(
            (
              BigInt(wallet?.reservedForOffers ?? '0') + BigInt(wallet?.reservedForFees ?? '0')
            ).toString(10),
          )}
        </strong>
      </p>
      <p>
        {label} disponível: <strong>{formatDecimal(wallet?.available ?? '0')}</strong>
      </p>
      <label>
        {label} oferecido ao outro jogador
        <input
          type="text"
          inputMode="numeric"
          value={value}
          disabled={disabled}
          data-testid={`trade-offer-${kind}`}
          onChange={(event) => onChange(event.target.value)}
        />
      </label>
      <p>
        O outro receberá: <strong>{formatDecimal(value)}</strong>
      </p>
      {/* The direct trade has no percentage. Said plainly, because a player
          who has used a marketplace will assume there is one. */}
      <p className="trade-note">
        {kind === 'gold'
          ? 'O OUTRO JOGADOR RECEBERÁ ESTE GOLD INTEGRALMENTE. O TRADE DIRETO NÃO POSSUI TAXA PERCENTUAL.'
          : 'O OUTRO JOGADOR RECEBERÁ ESTE CASH INTEGRALMENTE. TRANSFERIR CASH NÃO TRANSFERE O DIREITO DE RECRÉDITO DA COMPRA ORIGINAL.'}
      </p>
      <button
        type="button"
        className="neon-button"
        disabled={disabled}
        data-testid={`trade-commit-${kind}`}
        onClick={onCommit}
      >
        Atualizar oferta
      </button>
    </div>
  );
}

function FeeTab({
  trade,
  mineFeePoints,
  theirFeePoints,
  gold,
  cash,
  disabled,
  onGold,
  onCash,
  onCommit,
}: {
  trade: TradeWire;
  mineFeePoints: string;
  theirFeePoints: string;
  gold: string;
  cash: string;
  disabled: boolean;
  onGold: (value: string) => void;
  onCash: (value: string) => void;
  onCommit: () => void;
}) {
  return (
    <div className="trade-fee" data-testid="trade-fee">
      <p className="trade-note">
        TAXA OBRIGATÓRIA — {formatDecimal(DIRECT_TRADE_FEE_POINTS.toString(10))} Gold, ou{' '}
        {formatDecimal((DIRECT_TRADE_FEE_POINTS / BigInt(trade.fee.cashPointValue)).toString(10))}{' '}
        Cash, ou uma combinação equivalente.
      </p>
      {/* Two fields, never one: a contribution is Gold AND Cash, and mixing
          them in a single box is how somebody pays the wrong currency. */}
      <label>
        Sua contribuição em Gold
        <input
          type="text"
          inputMode="numeric"
          value={gold}
          disabled={disabled}
          data-testid="trade-fee-gold"
          onChange={(event) => onGold(event.target.value)}
        />
      </label>
      <label>
        Sua contribuição em Cash
        <input
          type="text"
          inputMode="numeric"
          value={cash}
          disabled={disabled}
          data-testid="trade-fee-cash"
          onChange={(event) => onCash(event.target.value)}
        />
      </label>
      <p>
        Seus pontos: <strong data-testid="trade-fee-mine">{formatDecimal(mineFeePoints)}</strong>
      </p>
      <p>
        Pontos do outro:{' '}
        <strong data-testid="trade-fee-theirs">{formatDecimal(theirFeePoints)}</strong>
      </p>
      <p>
        Total:{' '}
        <strong data-testid="trade-fee-total">
          {formatDecimal(trade.fee.totalPoints)} / {formatDecimal(trade.fee.requiredPoints)}
        </strong>
      </p>
      <p className={trade.fee.complete ? 'trade-accepted' : 'trade-pending'}>
        {trade.fee.complete
          ? 'TAXA COMPLETA'
          : `FALTAM ${formatDecimal(trade.fee.remainingPoints)} PONTOS`}
      </p>
      <button
        type="button"
        className="neon-button"
        disabled={disabled}
        data-testid="trade-fee-commit"
        onClick={onCommit}
      >
        Atualizar contribuição
      </button>
    </div>
  );
}
